/**
 * CustomDefenseManager Component
 * Allows premium users to save and load custom defenses (per-position coverage assignments).
 * Mirrors the SavedPlays.tsx save/load pattern for offensive plays, including the update-or-create
 * choice: a defense that came from a load is treated as an edit of that defense, not as new work.
 */

import { useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../amplify/data/resource';
import { usePremiumStatus } from '../hooks';
import Modal from './Modal';
import { throwOnErrors } from '../utils';
import type { SaveTargetHandle } from '../hooks';
import { useCustomDefenses } from '../context';
import { CustomDefense, CustomDefenseAssignments, FormationType, CoverageType, DefensePositionId } from '../types';
import { DEFENSE_POSITION_IDS, DEFENSE_POSITION_LABELS, getAssignmentOption, classifyCustomDefense } from '../rules';
import './CustomDefenseManager.css';

const client = generateClient<Schema>();

interface StoredCustomDefense {
  id: string;
  name: string;
  formation: string;
  cb1Coverage: string;
  dblb1Coverage: string;
  fsCoverage: string;
  ssCoverage: string;
  dblb2Coverage: string;
  cb2Coverage: string;
  lbCoverage: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface DefensePayload {
  formation: FormationType;
  assignments: CustomDefenseAssignments;
}

interface CustomDefenseManagerProps {
  currentDefense: DefensePayload;
  onLoadDefense: (defense: DefensePayload) => void;
  /** Which saved defense the working copy came from. Owned by the page, as in SavedPlays. */
  saveTarget: SaveTargetHandle<DefensePayload>;
  disabled?: boolean;
}

/** Maps between the flat per-position backend fields and the Assignments record shape. */
function assignmentsToFields(assignments: CustomDefenseAssignments) {
  return {
    cb1Coverage: assignments.cb1,
    dblb1Coverage: assignments.dblb1,
    fsCoverage: assignments.fs,
    ssCoverage: assignments.ss,
    dblb2Coverage: assignments.dblb2,
    cb2Coverage: assignments.cb2,
    lbCoverage: assignments.lb,
  };
}

function fieldsToAssignments(defense: StoredCustomDefense): CustomDefenseAssignments {
  return {
    cb1: defense.cb1Coverage as CoverageType,
    dblb1: defense.dblb1Coverage as CoverageType,
    fs: defense.fsCoverage as CoverageType,
    ss: defense.ssCoverage as CoverageType,
    dblb2: defense.dblb2Coverage as CoverageType,
    cb2: defense.cb2Coverage as CoverageType,
    lb: defense.lbCoverage as CoverageType,
  };
}

function DefensePreview({ assignments }: { assignments: CustomDefenseAssignments }) {
  return (
    <div className="defense-preview">
      {DEFENSE_POSITION_IDS.map((position: DefensePositionId) => {
        const coverage = assignments[position];
        const option = getAssignmentOption(position, coverage);
        return (
          <div className="preview-row" key={position}>
            {DEFENSE_POSITION_LABELS[position]}: {option?.label ?? coverage}
          </div>
        );
      })}
    </div>
  );
}

function CustomDefenseManager({ currentDefense, onLoadDefense, saveTarget, disabled = false }: CustomDefenseManagerProps) {
  const { isPremium, isLoading: authLoading } = usePremiumStatus();
  const { defenses, removeDefenseLocally, addOrUpdateDefenseLocally } = useCustomDefenses();
  const { target, isDirty, track, forget } = saveTarget;
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [defenseName, setDefenseName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  /** 'choose' offers update-vs-new; 'create' is the name-and-save form. */
  const [saveMode, setSaveMode] = useState<'choose' | 'create'>('create');

  const openSaveModal = () => {
    setError(null);
    setDefenseName('');
    setSaveMode(target ? 'choose' : 'create');
    setShowSaveModal(true);
  };

  const finishSave = (message: string) => {
    setSaveSuccess(message);
    setTimeout(() => {
      setSaveSuccess(null);
      setShowSaveModal(false);
    }, 1500);
  };

  const snapshotOf = (defense: DefensePayload): DefensePayload => ({
    formation: defense.formation,
    assignments: { ...defense.assignments },
  });

  const handleSave = async () => {
    if (!defenseName.trim()) {
      setError('Please enter a defense name');
      return;
    }

    const trimmedName = defenseName.trim();
    const isDuplicate = defenses.some((d) => d.name === trimmedName);
    if (isDuplicate) {
      setError('That name is already in use.');
      return; // defenseName input is untouched, so it's retained for editing (Req 9.2)
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: created, errors } = await client.mutations.saveCustomDefense({
        name: trimmedName,
        formation: currentDefense.formation,
        ...assignmentsToFields(currentDefense.assignments),
      });

      // A GraphQL error resolves rather than throwing, so without these checks the code reached
      // finishSave() and reported "✓ Defense saved!" for a defense that was never written.
      throwOnErrors(errors);
      if (!created) throw new Error('The defense was not saved. Please try again.');

      addOrUpdateDefenseLocally({
        id: created.id,
        name: created.name,
        formation: (created.formation as FormationType) ?? 'doubles',
        assignments: fieldsToAssignments(created as StoredCustomDefense),
        createdAt: created.createdAt,
        updatedAt: created.updatedAt,
      });
      // The new defense becomes the one being worked from, so the next save offers to update it.
      track({ id: created.id, name: created.name, snapshot: snapshotOf(currentDefense) });

      setDefenseName('');
      finishSave('✓ Defense saved!');
    } catch (err) {
      console.error('Error saving custom defense:', err);
      setError(err instanceof Error ? err.message : 'Failed to save defense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!target) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data: updated, errors } = await client.mutations.editCustomDefense({
        id: target.id,
        formation: currentDefense.formation,
        ...assignmentsToFields(currentDefense.assignments),
      });
      throwOnErrors(errors);

      if (updated) {
        addOrUpdateDefenseLocally({
          id: updated.id,
          name: updated.name,
          formation: (updated.formation as FormationType) ?? 'doubles',
          assignments: fieldsToAssignments(updated as StoredCustomDefense),
          createdAt: updated.createdAt,
          updatedAt: updated.updatedAt,
        });
      }

      // Re-snapshot so it reads as clean again rather than still claiming unsaved changes.
      track({ ...target, snapshot: snapshotOf(currentDefense) });
      finishSave('✓ Defense updated!');
    } catch (err) {
      console.error('Error updating custom defense:', err);
      setError(err instanceof Error ? err.message : 'Failed to update defense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoad = (defense: CustomDefense) => {
    const payload = snapshotOf({ formation: defense.formation, assignments: defense.assignments });
    onLoadDefense(payload);
    if (defense.id) {
      track({ id: defense.id, name: defense.name, snapshot: payload });
    }
    setShowLoadModal(false);
  };

  const handleDelete = async (defenseId: string) => {
    try {
      await client.mutations.removeCustomDefense({ id: defenseId });
      removeDefenseLocally(defenseId);
      // Nothing to update anymore — a later save has to create.
      forget(defenseId);
    } catch (err) {
      console.error('Error deleting custom defense:', err);
    }
  };

  if (authLoading) {
    return null;
  }

  // Non-premium: controls visible but locked. Disabled buttons fire no handler, so there is no
  // reachable path to a write; the modals are not rendered at all. See SavedPlays for the
  // reasoning in full.
  if (!isPremium) {
    return (
      <div className="custom-defense-manager defense-manager-locked">
        <div className="defense-manager-buttons">
          <button className="defense-manager-btn save-btn" disabled type="button" title="Premium feature">
            🔒 Save Defense
          </button>
          <button className="defense-manager-btn load-btn" disabled type="button" title="Premium feature">
            🔒 Load Defense
          </button>
        </div>
        <a className="defense-manager-upgrade-link" href="/pricing">
          Save custom defenses with Premium
        </a>
      </div>
    );
  }

  return (
    <div className="custom-defense-manager">
      <div className="defense-manager-buttons">
        <button
          className="defense-manager-btn save-btn"
          onClick={openSaveModal}
          disabled={disabled}
          type="button"
        >
          💾 Save Defense{isDirty ? ' *' : ''}
        </button>
        <button
          className="defense-manager-btn load-btn"
          onClick={() => setShowLoadModal(true)}
          disabled={disabled || defenses.length === 0}
          type="button"
        >
          📂 Load Defense {defenses.length > 0 && `(${defenses.length})`}
        </button>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <Modal
          title="Save Defense"
          onClose={() => setShowSaveModal(false)}
          overlayClassName="defense-manager-modal-overlay"
          panelClassName="defense-manager-modal"
        >
              {saveSuccess ? (
                <div className="save-success">{saveSuccess}</div>
              ) : saveMode === 'choose' && target ? (
                <>
                  <p className="save-choice-prompt">
                    {isDirty ? (
                      <>You've made changes to <strong>{target.name}</strong>.</>
                    ) : (
                      <>You're working from <strong>{target.name}</strong>, with no changes yet.</>
                    )}
                  </p>
                  {error && <div className="modal-error">{error}</div>}
                  <DefensePreview assignments={currentDefense.assignments} />
                  <div className="save-choice-actions">
                    <button
                      className="modal-save-btn"
                      onClick={handleUpdate}
                      disabled={isLoading || !isDirty}
                      title={isDirty ? undefined : 'Nothing has changed since you loaded it'}
                    >
                      {isLoading ? 'Updating...' : `Update "${target.name}"`}
                    </button>
                    <button
                      className="modal-secondary-btn"
                      onClick={() => {
                        setError(null);
                        setDefenseName('');
                        setSaveMode('create');
                      }}
                      disabled={isLoading}
                    >
                      Save as a new defense
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {target && (
                    <button
                      className="modal-back-btn"
                      onClick={() => {
                        setError(null);
                        setSaveMode('choose');
                      }}
                      disabled={isLoading}
                    >
                      ← Back
                    </button>
                  )}
                  <input
                    type="text"
                    placeholder="Enter defense name..."
                    value={defenseName}
                    onChange={(e) => setDefenseName(e.target.value)}
                    className="defense-name-input"
                    maxLength={50}
                    autoFocus
                  />
                  {error && <div className="modal-error">{error}</div>}
                  <DefensePreview assignments={currentDefense.assignments} />
                  <button
                    className="modal-save-btn"
                    onClick={handleSave}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Defense'}
                  </button>
                </>
              )}
        </Modal>
      )}

      {/* Load Modal */}
      {showLoadModal && (
        <Modal
          title="Load Defense"
          onClose={() => setShowLoadModal(false)}
          overlayClassName="defense-manager-modal-overlay"
          panelClassName="defense-manager-modal"
        >
              {isLoading ? (
                <div className="loading-defenses">Loading...</div>
              ) : defenses.length === 0 ? (
                <div className="no-defenses">No saved defenses yet</div>
              ) : (
                <ul className="defenses-list">
                  {defenses.map((defense) => (
                    <li key={defense.id} className={`defense-item${target?.id === defense.id ? ' defense-item-current' : ''}`}>
                      {/* A real button, not a div with onClick — loading was mouse-only. */}
                      <button
                        className="defense-info"
                        type="button"
                        onClick={() => handleLoad(defense)}
                      >
                        <span className="defense-name">
                          {defense.name}
                          {target?.id === defense.id && (
                            <span className="defense-current-badge">{isDirty ? 'edited' : 'loaded'}</span>
                          )}
                        </span>
                        <span className="defense-formation">{defense.formation}</span>
                        {classifyCustomDefense(defense.assignments) === 'draft' && (
                          <span className="defense-draft-badge">Draft</span>
                        )}
                      </button>
                      <button
                        className="defense-delete-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (defense.id) {
                            handleDelete(defense.id);
                          }
                        }}
                        aria-label={`Delete ${defense.name}`}
                      >
                        <span aria-hidden="true">🗑️</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
        </Modal>
      )}
    </div>
  );
}

export default CustomDefenseManager;
